import { Button, Divider, message, Result, Row, Col, Tabs, Popover } from 'antd'
import pick from 'lodash/pick'
import React, { useCallback, useState } from 'react'
import { useHistory, useParams, Link } from 'react-router-dom'
import api from '../../api'
import { useAsync } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import useAlertGeneratedEvent from '../../hooks/useAlertGeneratedEvent'
import { formatDateTime } from '../../lib/datetime'
import { useUnacknowledgedAlertsCount } from '../../redux/slices/alerts'
import { useIsAdmin, useCurrentUserRole } from '../../redux/slices/session'
import { toPath } from '../../routes'
import Comments from '../Comments'
import { ListError, PageHeader } from '../Common'
import ActionButtons from '../Common/ActionButtons'
import NavigationButtons from '../Common/NavigationButtons'

import PatientSummary from '../Common/PatientSummary'
import PDFPreview from '../Common/PDFPreview'
import Loading from '../Loading'
import DownloadButtons from '../Common/DownloadButtons'
import TransmissionSidebar from '../Common/TransmissionSidebar'

const { TabPane } = Tabs

export default function Show() {
  const { id } = useParams()
  const history = useHistory()
  const isAdmin = useIsAdmin()
  const isClinician = useCurrentUserRole() === 'clinician'

  const [conditions] = useQueryParams({})
  const { fromPractice } = conditions
  const fromPracticeParam = fromPractice ? `?fromPractice=${fromPractice}` : ''

  const { reset: setAlertsCount } = useUnacknowledgedAlertsCount()
  const [alert, setAlert] = useState({})
  const [practice, setPractice] = useState(null)

  const {
    event,
    sendAlertGeneratedToEMR,
    buttonState: {
      disabled: isSendToEMRDisabled,
      type: sendToEMRType,
      icon: sendToEMRIcon,
      loading: showSpinner,
      text: sendToEMRButtonText
    }
  } = useAlertGeneratedEvent(alert.id)

  const getPractice = useCallback(async practiceId => {
    if (practiceId) {
      try {
        const practiceData = await api.practices.getPractice(practiceId)
        setPractice(practiceData)
      } catch (err) {
        message.error('Error fetching practice:', err)
      }
    }
  }, [])

  const getAlert = useCallback(async () => {
    const alertData = await api.alerts.get(id, pick(conditions, 'fromPractice'))
    setAlert(alertData)

    // Fetch practice data if we have a practiceId
    if (alertData.patient?.practiceId) {
      await getPractice(alertData.patient.practiceId)
    }
  }, [conditions, id, getPractice])

  const { pending: isAckPending, execute: ack } = useAsync(async () => {
    try {
      await api.alerts.acknowledge(id, pick(conditions, 'fromPractice'))
      await getAlert()

      setAlertsCount()

      message.success('Alert successfully acknowledged')
    } catch (err) {
      message.error(
        `There was a problem acknowledging this alert: ${err.message}`,
      )
    }
  }, false)

  const { pending: isUnackPending, execute: unack } = useAsync(async () => {
    try {
      await api.alerts.unacknowledge(id, pick(conditions, 'fromPractice'))
      await getAlert()

      setAlertsCount()

      message.success('Alert successfully unacknowledged')
    } catch (err) {
      message.error(
        `There was a problem unacknowledging this alert: ${err.message}`,
      )
    }
  }, false)

  const { error, pending } = useAsync(getAlert)

  const alertPath = useCallback(
    alertId =>
      alertId && `${toPath.alerts.alert({ id: alertId })}${fromPracticeParam}`,
    [fromPracticeParam],
  )

  const handleDelete = useCallback(async () => {
    try {
      await api.alerts.destroy(id)
      message.success('Alert successfully removed')
      history.goBack()
    } catch (e) {
      message.error(`There was an error removing this alert: ${e.message}`)
    }
  }, [history, id])

  if (pending) {
    return <Loading large />
  }

  if (error) {
    return (
      <ListError
        message="There was an error loading this alert"
        description={error.message}
      />
    )
  }

  const { patient, nextAlertId, previousAlertId } = alert

  return (
    <div>
      <NavigationButtons
        nextPath={alertPath(nextAlertId)}
        prevPath={alertPath(previousAlertId)}
      />
      <PageHeader
        title={`${patient.firstName} ${patient.lastName}`}
        subTitle={
          alert.acknowledgedAt &&
          `Acknowledged ${formatDateTime(alert.acknowledgedAt)}`
        }
        onBack={history.goBack}
        extra={
          <>
            {alert.acknowledgedAt && (
              <Button loading={isUnackPending} onClick={unack}>
                Unacknowledge
              </Button>
            )}
            {alert?.acknowledgedAt &&
              practice?.integrationConfig?.alerts?.enabled === true &&
              practice?.integrationConfig?.alerts?.type === 'manual' && (
                <Popover
                  content="Failed to send, contact support."
                  trigger={event?.status === 'failed' ? 'hover' : ''}
                  placement="top"
                >
                  {/* Send to EMR button */}
                  <Button
                    type={sendToEMRType}
                    icon={sendToEMRIcon}
                    loading={showSpinner}
                    onClick={isSendToEMRDisabled ? undefined : sendAlertGeneratedToEMR}
                    style={{
                      cursor: isSendToEMRDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sendToEMRButtonText}
                  </Button>
                </Popover>
              )}
            {!isClinician && (
              <Link
                key="edit-patient"
                to={toPath.patients.patient.edit({ id: patient.id })}
              >
                <Button>Edit Patient</Button>
              </Link>
            )}
            <Link key="edit-alert" to={toPath.alerts.alert.edit({ id })}>
              <Button>Edit Alert</Button>
            </Link>
            {!isClinician && <DownloadButtons resource={alert} />}
          </>
        }
      />
      <PatientSummary patient={patient} />
      <Divider />
      {alert.acknowledgedAt || isAdmin ? (
        <>
          <Row gutter={16}>
            <Col span={16}>
              <Tabs defaultActiveKey="1" className="tabs">
                {alert?.filePath && (
                  <TabPane tab="Report" key="report">
                    <PDFPreview path={alert.filePath} showAllPages />
                  </TabPane>
                )}
                {alert?.rawFilePath && (
                  <TabPane tab="Transmission" key="transmission">
                    <PDFPreview path={alert.rawFilePath} showAllPages />
                  </TabPane>
                )}
              </Tabs>
            </Col>
            <Col span={8}>
              <TransmissionSidebar patient={alert.patient} />
            </Col>
          </Row>
          <ActionButtons onDelete={handleDelete} />
          <Comments collectionName="alerts" id={alert.id} />
        </>
      ) : (
        <Result
          title="You need to acknowledge this alert before you can see its contents"
          extra={
            <Button type="primary" loading={isAckPending} onClick={ack}>
              Acknowledge
            </Button>
          }
        />
      )}
    </div>
  )
}
